import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { ReactNode } from "react";

import { useCountries } from "../../../catalogs/countries";
import { ChoosingModal } from "../../../components/ui-kit/choosing-modal/choosing-modal";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import {
  patientStatuses,
  monitoringTypes,
} from "../../../constants/enum-to-text";
import { SelectionMode } from "../../../models/data-model";
import {
  getCountryOptions,
  getRegionOptions,
  regionDisabled,
  regionPredicate,
} from "../../../utils/filter-presets";
import { Patients } from "../../patients/patients";

export const PatientsModal = observer(function PatientsModal(props: {
  model: Patients;
  mode: SelectionMode;
  preselected?: number[];
  filter?: Record<string, any[]>;
  title: ReactNode;
  onConfirm: (params: { selected: any[] }) => void;
  onClose: () => void;
}) {
  const { t } = useLingui();
  const countries = useCountries();
  const initialColumnFilters = props.filter?.country?.length
    ? [{ id: "country", value: props.filter.country }]
    : [];

  return (
    <ChoosingModal
      model={props.model}
      onClose={props.onClose}
      onConfirm={props.onConfirm}
      title={props.title}
      fillHeight
      table={
        <DataTable
          model={props.model}
          itemKeyField="patient_id"
          isLoading={props.model.loading}
          fillHeight
          canSelect={props.mode}
          preselected={props.preselected}
          filterNoun={(count) =>
            t`${plural(count, { one: "patient", other: "patients" })}`
          }
          initialColumnFilters={initialColumnFilters}
          columns={[
            {
              header: <Trans>Patient ID</Trans>,
              attr: "patient_id",
              type: "number",
              sort: true,
              initSort: "asc",
              className: "font-bold",
              filter: {
                kind: "text",
                group: t`Identity`,
                placeholder: t`Search…`,
              },
            },
            {
              header: <Trans>Name</Trans>,
              attr: "full_name",
              type: "string",
              sort: true,
              align: "left",
              filter: {
                kind: "text",
                group: t`Identity`,
                placeholder: t`Search…`,
              },
            },
            {
              header: <Trans>Status</Trans>,
              attr: "patient_status",
              type: "enum",
              values: patientStatuses,
              filter: {
                kind: "multi",
                group: t`Status`,
                options: new Map(Object.entries(patientStatuses)),
              },
            },
            {
              header: <Trans>Monitoring</Trans>,
              attr: "monitoring_type",
              type: "enum",
              values: monitoringTypes,
              filter: {
                kind: "multi",
                group: t`Status`,
                options: new Map(Object.entries(monitoringTypes)),
              },
            },
            {
              header: <Trans>Country</Trans>,
              attr: "country",
              type: "string",
              align: "left",
              filter: {
                kind: "multi",
                group: t`Location`,
                options: getCountryOptions,
              },
            },
            {
              header: <Trans>Region</Trans>,
              attr: "region",
              align: "left",
              filter: {
                kind: "multi",
                group: t`Location`,
                options: (table) => getRegionOptions(table, countries),
                disabled: regionDisabled,
                disabledMessage: t`Select a country first`,
                predicate: regionPredicate,
              },
              cell: ({ item }) =>
                item.region != null
                  ? countries.getRegionSchema(item.country, item.region)
                      ?.region_name
                  : undefined,
            },
          ]}
        />
      }
    />
  );
});
